import React, { useMemo } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { PageHeader, DataTable } from '@/components/shared'
import { emailAuditLogService } from '@/services/emailAuditLogService'
import { formatDateTime } from '@/lib/dateUtils'

const ENTITY_LABELS = {
  contact: 'Contact',
  custom_field: 'Custom Field',
  segment: 'Segment',
  template: 'Template',
  campaign: 'Campaign',
  suppression: 'Suppression',
  settings: 'Settings',
}

// Colors are derived from the verb in the action string (e.g. "contact.created" -> "created")
// rather than a hardcoded per-action lookup table, so a newly added action type always gets a
// sensible color instead of silently falling through unmapped.
const verbVariant = (verb) => {
  if (verb === 'created' || verb === 'imported' || verb === 'added') return 'success'
  if (verb === 'updated') return 'warning'
  if (verb === 'deleted' || verb === 'cancelled' || verb === 'removed') return 'destructive'
  return 'secondary'
}

const actionLabel = (action) => {
  const [, verb] = action.split('.')
  if (!verb) return action
  return verb.charAt(0).toUpperCase() + verb.slice(1)
}

const AuditLog = () => {
  const { data, isLoading } = useQuery(
    'email-audit-log',
    () => emailAuditLogService.getAuditLogs({ limit: 100 }),
    { onError: () => toast.error('Failed to fetch audit log') }
  )
  const auditLogs = data?.data?.auditLogs || []

  const columns = useMemo(() => [
    {
      accessorKey: 'action',
      header: 'Action',
      cell: ({ row }) => {
        const verb = row.original.action.split('.')[1]
        return <Badge variant={verbVariant(verb)}>{actionLabel(row.original.action)}</Badge>
      },
    },
    {
      id: 'entity',
      header: 'Entity',
      accessorFn: (row) => `${ENTITY_LABELS[row.entity_type] || row.entity_type} — ${row.entity_name || ''}`,
      cell: ({ row }) => (
        <span>
          <span className="text-muted-foreground">{ENTITY_LABELS[row.original.entity_type] || row.original.entity_type}</span>
          {row.original.entity_name ? <span> — {row.original.entity_name}</span> : null}
        </span>
      ),
    },
    {
      id: 'user',
      header: 'User',
      accessorFn: (row) => row.actor?.email || 'System',
      cell: ({ row }) => row.original.actor?.email || <span className="text-muted-foreground">System</span>,
    },
    {
      accessorKey: 'created_at',
      header: 'Date',
      cell: ({ row }) => formatDateTime(row.original.created_at),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Log"
        description="A record of who did what across contacts, campaigns, templates, segments and settings"
      />

      <DataTable
        columns={columns}
        data={auditLogs}
        loading={isLoading}
        searchPlaceholder="Search audit log..."
        emptyMessage="No audit log entries yet."
        pageSize={20}
      />
    </div>
  )
}

export default AuditLog
